/**
 * Indexing of session notes.
 *
 * Two questions are asked of notes, and each screen asks only one of them: "what was written
 * about this day?" while a roll call is being taken, and "what has been written about this
 * person?" on their page. Both are answered from one pass over the records, built alongside
 * the attendance index it sits next to.
 */

import { noteId, type IsoDate, type Note, type PersonId, type ProjectId } from './types.js';

/** Lookups over a project's live notes. */
export interface NoteIndex {
  /** Notes about a day itself, by date. */
  byDate: ReadonlyMap<IsoDate, Note>;
  /** Notes about one person on one day, by `${projectId}:${date}:${personId}`. */
  byPersonDate: ReadonlyMap<string, Note>;
  /** Every note about one person, ascending by date. */
  byPerson: ReadonlyMap<PersonId, Note[]>;
  /** Every date that carries at least one note, about the day or about someone on it. */
  dates: ReadonlySet<IsoDate>;
}

/** An index over no notes at all. */
export function emptyNoteIndex(): NoteIndex {
  return { byDate: new Map(), byPersonDate: new Map(), byPerson: new Map(), dates: new Set() };
}

/**
 * Indexes live notes, keeping the most recently updated record per key.
 *
 * Soft-deleted notes are dropped, which is how clearing the text removes a note.
 */
export function indexNotes(notes: readonly Note[]): NoteIndex {
  const live = new Map<string, Note>();
  for (const note of notes) {
    const existing = live.get(note.id);
    if (existing !== undefined && existing.updatedAt > note.updatedAt) continue;
    if (note.deletedAt !== null || note.text === '') live.delete(note.id);
    else live.set(note.id, note);
  }

  const byDate = new Map<IsoDate, Note>();
  const byPersonDate = new Map<string, Note>();
  const byPerson = new Map<PersonId, Note[]>();
  const dates = new Set<IsoDate>();
  for (const note of live.values()) {
    dates.add(note.date);
    if (note.personId === null) {
      byDate.set(note.date, note);
      continue;
    }
    byPersonDate.set(note.id, note);
    const own = byPerson.get(note.personId);
    if (own === undefined) byPerson.set(note.personId, [note]);
    else own.push(note);
  }
  for (const own of byPerson.values()) own.sort((left, right) => left.date.localeCompare(right.date));

  return { byDate, byPersonDate, byPerson, dates };
}

/** Reads the note about `date` itself, or `undefined` when none was written. */
export function dayNote(index: NoteIndex, date: IsoDate): Note | undefined {
  return index.byDate.get(date);
}

/** Reads the note about one person on one day, or `undefined` when none was written. */
export function personNote(
  index: NoteIndex,
  projectId: ProjectId,
  personId: PersonId,
  date: IsoDate,
): Note | undefined {
  return index.byPersonDate.get(noteId(projectId, date, personId));
}

/** Lists every note about `personId`, ascending by date. */
export function notesAbout(index: NoteIndex, personId: PersonId): readonly Note[] {
  return index.byPerson.get(personId) ?? [];
}
